import { useCallback, useEffect, useRef, useState } from 'react';
import { Edit, Image, Plus, Search, Trash2, Upload } from 'lucide-react';
import toast from 'react-hot-toast';
import { createPost, deletePost, listPosts, updatePost, uploadPostImage } from '../api/articles';
import { getErrorMessage } from '../api/response';
import Button from '../components/Button';
import ConfirmDialog from '../components/ConfirmDialog';
import FormField, { inputClassName } from '../components/FormField';
import Modal from '../components/Modal';
import PageHeader from '../components/PageHeader';
import RichTextEditor from '../components/RichTextEditor';
import StatusBadge from '../components/StatusBadge';
import Table from '../components/Table';
import { LoadingState } from '../components/PageState';
import useDebouncedValue from '../hooks/useDebouncedValue';
import { dateTime } from '../utils/format';
import { resizeImageFile } from '../utils/images';
import { getStatusConfig } from '../utils/status';

const postStatus = {
  draft: { label: 'Borrador', color: 'slate' },
  active: { label: 'Publicado', color: 'green' },
  inactive: { label: 'Inactivo', color: 'slate' }
};

const emptyForm = {
  title: '',
  category: '',
  image_url: '',
  description: '',
  status: 'draft'
};

const applyMobileImageStyles = (html = '') => {
  if (!html || typeof DOMParser === 'undefined') {
    return html;
  }

  const parser = new DOMParser();
  const documentHtml = parser.parseFromString(html, 'text/html');

  documentHtml.body.querySelectorAll('img').forEach((image) => {
    image.style.width = '100%';
    image.style.maxWidth = '100%';
    image.style.height = 'auto';
    image.style.display = 'block';
  });

  return documentHtml.body.innerHTML;
};

const htmlHasContent = (value = '') => {
  const text = String(value)
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .trim();

  return Boolean(text) || /<img\b/i.test(value);
};

const MobilePostPreview = ({ form }) => {
  const contentHtml = applyMobileImageStyles(form.description);
  const content = htmlHasContent(contentHtml)
    ? contentHtml
    : '<p>El contenido del post aparecerá aquí mientras editas.</p>';

  return (
    <aside className="xl:sticky xl:top-0">
      <div className="mx-auto w-full max-w-[360px] rounded-[2rem] border border-gray-300 bg-gray-950 p-2 shadow-xl">
        <div className="overflow-hidden rounded-[1.55rem] bg-gray-50">
          <div className="flex items-center justify-between bg-white px-4 py-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-blue-600">Inicio</p>
              <p className="text-sm font-semibold text-gray-950">Información importante</p>
            </div>
            <span className="h-8 w-8 rounded-full bg-blue-100" />
          </div>
          <div className="max-h-[640px] overflow-y-auto px-4 py-4">
            <article className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
              {form.image_url ? (
                <img src={form.image_url} alt="" className="h-44 w-full object-cover" />
              ) : (
                <div className="flex h-44 items-center justify-center bg-gray-100 text-gray-400">
                  <Image className="h-8 w-8" />
                </div>
              )}
              <div className="space-y-3 p-4">
                <div className="flex items-center justify-between gap-2">
                  <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700">
                    {form.category || 'General'}
                  </span>
                  <span className="text-[11px] text-gray-400">Hoy</span>
                </div>
                <h1 className="text-xl font-bold leading-tight text-gray-950">
                  {form.title || 'Título del post'}
                </h1>
                <div
                  className="text-sm leading-6 text-gray-700 [&_a]:text-blue-700 [&_blockquote]:border-l-4 [&_blockquote]:border-blue-200 [&_blockquote]:pl-3 [&_blockquote]:text-gray-600 [&_h2]:mb-2 [&_h2]:mt-4 [&_h2]:text-lg [&_h2]:font-bold [&_img]:my-3 [&_img]:w-full [&_img]:rounded-lg [&_li]:ml-5 [&_ol]:list-decimal [&_p]:mb-3 [&_strong]:font-semibold [&_ul]:list-disc"
                  dangerouslySetInnerHTML={{ __html: content }}
                />
              </div>
            </article>
          </div>
        </div>
      </div>
    </aside>
  );
};

const Posts = () => {
  const [posts, setPosts] = useState([]);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [confirm, setConfirm] = useState(null);
  const coverInputRef = useRef(null);
  const debouncedSearch = useDebouncedValue(search.trim());

  const loadPosts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await listPosts({
        search: debouncedSearch || undefined,
        status: status || undefined,
        limit: 100
      });
      setPosts(data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudieron cargar los posts'));
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, status]);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  const openPost = (post = null) => {
    setEditing(post);
    setForm(post ? {
      title: post.title || '',
      category: post.category || '',
      image_url: post.image_url || '',
      description: post.description || post.content_html || '',
      status: post.status || 'draft'
    } : emptyForm);
    setModalOpen(true);
  };

  const uploadEditorImage = async (file) => {
    const resized = await resizeImageFile(file, {
      maxWidth: 1200,
      maxHeight: 1200,
      quality: 0.86
    });
    const image = await uploadPostImage(resized);
    return image.image_url || image.url;
  };

  const handleCoverUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) return;

    try {
      setUploadingCover(true);
      const imageUrl = await uploadEditorImage(file);
      setForm((current) => ({ ...current, image_url: imageUrl }));
      toast.success('Imagen de portada subida');
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo subir la imagen'));
    } finally {
      setUploadingCover(false);
    }
  };

  const savePost = async (event) => {
    event.preventDefault();

    try {
      setSaving(true);
      const payload = {
        title: form.title,
        category: form.category || null,
        image_url: form.image_url || null,
        description: htmlHasContent(form.description) ? applyMobileImageStyles(form.description) : null,
        status: form.status
      };

      if (editing) {
        await updatePost(editing.id, payload);
        toast.success('Post actualizado');
      } else {
        await createPost(payload);
        toast.success('Post creado');
      }

      setModalOpen(false);
      loadPosts();
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo guardar el post'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm) return;

    try {
      setSaving(true);
      await deletePost(confirm.id);
      toast.success('Post inactivado');
      setConfirm(null);
      loadPosts();
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo inactivar el post'));
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      key: 'title',
      label: 'Post',
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 shrink-0 overflow-hidden rounded-md bg-gray-100">
            {row.image_url ? <img src={row.image_url} alt="" className="h-full w-full object-cover" /> : null}
          </div>
          <div>
            <p className="font-semibold text-gray-900">{row.title}</p>
            <p className="text-xs text-gray-500">{row.category || 'Sin categoria'}</p>
          </div>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Estado',
      render: (row) => {
        const config = getStatusConfig(postStatus, row.status);
        return <StatusBadge label={config.label} color={config.color} />;
      }
    },
    { key: 'created_at', label: 'Creado', render: (row) => dateTime(row.created_at) },
    {
      key: 'actions',
      label: 'Acciones',
      render: (row) => (
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => openPost(row)} className="rounded-md p-2 text-blue-700 hover:bg-blue-50" title="Editar">
            <Edit className="h-4 w-4" />
          </button>
          <button type="button" onClick={() => setConfirm(row)} className="rounded-md p-2 text-red-700 hover:bg-red-50" title="Inactivar">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Posts de inicio"
        description="Contenido tipo blog que aparece en Información importante dentro de la app."
        actions={<Button icon={Plus} onClick={() => openPost()}>Nuevo post</Button>}
      />

      <section className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex flex-col gap-3 md:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className={`${inputClassName} pl-9`}
              placeholder="Buscar por titulo, categoria o contenido"
            />
          </div>
          <select className={inputClassName} value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">Todos los estados</option>
            <option value="active">Publicados</option>
            <option value="draft">Borradores</option>
            <option value="inactive">Inactivos</option>
          </select>
        </div>
      </section>

      <section className="rounded-lg border border-gray-200 bg-white">
        {loading ? (
          <div className="p-5"><LoadingState rows={5} /></div>
        ) : (
          <Table columns={columns} data={posts} emptyMessage="No hay posts registrados" />
        )}
      </section>

      <Modal
        open={modalOpen}
        title={editing ? 'Editar post' : 'Nuevo post'}
        onClose={() => setModalOpen(false)}
        size="full"
        footer={
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setModalOpen(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button type="submit" form="post-form" disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </div>
        }
      >
        <div className="grid gap-6 xl:grid-cols-3">
          <form id="post-form" onSubmit={savePost} className="grid gap-4 md:grid-cols-2 xl:col-span-2">
            <FormField label="Titulo" required>
              <input
                className={inputClassName}
                value={form.title}
                onChange={(event) => setForm({ ...form, title: event.target.value })}
                required
              />
            </FormField>
            <FormField label="Categoria">
              <input
                className={inputClassName}
                value={form.category}
                onChange={(event) => setForm({ ...form, category: event.target.value })}
                placeholder="Ej: Salud, Consejos, Novedades"
              />
            </FormField>
            <div>
              <span className="mb-1.5 block text-sm font-medium text-gray-700">Imagen portada</span>
              <div className="flex gap-2">
                <input
                  className={inputClassName}
                  value={form.image_url}
                  onChange={(event) => setForm({ ...form, image_url: event.target.value })}
                  placeholder="https://..."
                />
                <button
                  type="button"
                  title="Subir portada"
                  className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={() => coverInputRef.current?.click()}
                  disabled={uploadingCover}
                >
                  <Upload className="h-4 w-4" />
                </button>
              </div>
              <input
                ref={coverInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handleCoverUpload}
              />
            </div>
            <FormField label="Estado">
              <select
                className={inputClassName}
                value={form.status}
                onChange={(event) => setForm({ ...form, status: event.target.value })}
              >
                <option value="draft">Borrador</option>
                <option value="active">Publicado</option>
                <option value="inactive">Inactivo</option>
              </select>
            </FormField>
            <div className="md:col-span-2">
              <span className="mb-1.5 block text-sm font-medium text-gray-700">Contenido</span>
              <RichTextEditor
                value={form.description}
                onChange={(description) => setForm((current) => ({ ...current, description }))}
                onUploadImage={uploadEditorImage}
                onUploadError={(error) => toast.error(getErrorMessage(error, 'No se pudo subir la imagen'))}
                placeholder="Escribe el post..."
              />
            </div>
          </form>

          <MobilePostPreview form={form} />
        </div>
      </Modal>

      <ConfirmDialog
        open={Boolean(confirm)}
        title="Inactivar post"
        message={`El post ${confirm?.title} dejara de verse en la app.`}
        confirmLabel="Inactivar"
        loading={saving}
        onCancel={() => setConfirm(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default Posts;
